/**
 * ── useNavigationVisibility ──
 * Filters navigation items by module entitlement and tenant eligibility.
 * Works with both role-specific nav configs and the default sidebar nav.
 *
 * Usage:
 *   const { isModuleVisible, filterNavItems } = useNavigationVisibility();
 */

import { useCallback, useMemo } from "react";
import { useTenantEntitlements } from "@/hooks/useTenantEntitlements";
import { MODULE_REGISTRY, type NavGroup, NAV_GROUP_ORDER } from "@/config/moduleRegistry";

export interface NavigationVisibility {
  /** Check if a module key should be visible in nav */
  isModuleVisible: (moduleKey: string | undefined) => boolean;
  /** Filter an array of nav items that have a module? property */
  filterNavItems: <T extends { module?: string; children?: Array<{ module?: string }> }>(items: T[]) => T[];
  /** Get visible nav groups in order */
  visibleNavGroups: NavGroup[];
}

export function useNavigationVisibility(): NavigationVisibility {
  const { moduleMap, tenantType, isPlatformAdmin } = useTenantEntitlements();

  const isModuleVisible = useCallback(
    (moduleKey: string | undefined): boolean => {
      // No module key = always visible (e.g., Dashboard, Settings)
      if (!moduleKey) return true;

      // Platform admins see everything
      if (isPlatformAdmin) return true;

      // Check the resolved module map
      const state = moduleMap.get(moduleKey);
      if (!state) {
        // Module not in registry — check if it's in the old workspace_modules system
        // by looking at tenant eligibility from the registry
        const def = MODULE_REGISTRY[moduleKey];
        if (!def) return true; // Unknown module key, don't hide
        return def.eligibleTenants.includes(tenantType);
      }

      // Hide if behavior says hide, otherwise show if has access or is teasable
      return state.entitlement.behavior !== "hide";
    },
    [moduleMap, tenantType, isPlatformAdmin],
  );

  const filterNavItems = useCallback(
    <T extends { module?: string; children?: Array<{ module?: string }> }>(items: T[]): T[] => {
      return items
        .filter((item) => isModuleVisible(item.module))
        .map((item) => {
          if (item.children) {
            return {
              ...item,
              children: item.children.filter((child) => isModuleVisible(child.module)),
            };
          }
          return item;
        })
        .filter((item) => {
          // Remove parent items whose children were all filtered out
          if (item.children && item.children.length === 0) return false;
          return true;
        });
    },
    [isModuleVisible],
  );

  const visibleNavGroups = useMemo(() => {
    const groups = new Set<NavGroup>();
    for (const [, state] of moduleMap) {
      if (state.isEligible && state.entitlement.behavior !== "hide") {
        groups.add(state.definition.navGroup);
      }
    }
    return Array.from(groups).sort((a, b) => (NAV_GROUP_ORDER[a] ?? 99) - (NAV_GROUP_ORDER[b] ?? 99));
  }, [moduleMap]);

  return useMemo(
    () => ({ isModuleVisible, filterNavItems, visibleNavGroups }),
    [isModuleVisible, filterNavItems, visibleNavGroups],
  );
}
